import React, { useState, useEffect, useRef } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  PaperPlaneTilt,
  SpinnerGap,
  Sparkle,
  Plus,
  Clock,
  ArrowRight,
  ForkKnife,
  MapPin,
  User
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const SUGGESTIONS = [
  "I'm craving seafood in Camps Bay for 4 people",
  "Where should I go for a special anniversary dinner?",
  "Best new restaurants that opened recently?",
  "Recommend something under R300pp in the City Bowl",
  "I want to try a cuisine I haven't had before",
  "What's on my wishlist that I should visit next?",
];

const Concierge = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const api = createApiClient(token);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const res = await api.get('/concierge/sessions');
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadSession = async (sid) => {
    try {
      const res = await api.get(`/concierge/sessions/${sid}`);
      setMessages(res.data.messages || []);
      setSessionId(sid);
      setShowSidebar(false);
    } catch (e) {
      toast.error('Failed to load conversation');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSidebar(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim(), at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/concierge/chat', {
        message: text.trim(),
        session_id: sessionId
      });

      const assistantMsg = {
        role: 'assistant',
        content: res.data.response,
        at: new Date().toISOString(),
        mentioned: res.data.mentioned_restaurants
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (!sessionId) {
        setSessionId(res.data.session_id);
      }

      loadSessions();
    } catch (error) {
      toast.error('Concierge is temporarily unavailable');
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="h-[calc(100vh-64px)] flex pb-20 md:pb-0" data-testid="concierge-page">
      {/* Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className="w-[280px] border-r border-[#EAE8E3] bg-white flex-shrink-0 overflow-y-auto absolute md:relative z-20 h-full"
            data-testid="chat-sidebar"
          >
            <div className="p-4">
              <Button onClick={startNewChat} className="w-full btn-primary mb-4" size="sm" data-testid="new-chat-btn">
                <Plus weight="bold" className="w-4 h-4 mr-2" />
                New conversation
              </Button>
              <div className="space-y-1">
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => loadSession(s.session_id)}
                    className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
                      sessionId === s.session_id ? 'bg-[#F5F1EC]' : 'hover:bg-[#F9F8F5]'
                    }`}
                  >
                    <p className="text-[#1A1918] truncate">{s.preview || 'New conversation'}</p>
                    <p className="text-[10px] text-[#9A9590] mt-0.5 flex items-center gap-1">
                      <Clock weight="fill" className="w-3 h-3" />
                      {s.updated_at ? new Date(s.updated_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : ''}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="border-b border-[#EAE8E3] px-4 py-3 flex items-center gap-3 bg-white">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="p-2 rounded-lg hover:bg-[#F5F1EC] transition-colors"
            data-testid="toggle-sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7A7873" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#A94438] to-[#C4A35A] flex items-center justify-center">
            <Sparkle weight="fill" className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-[#1A1918] text-sm">Dining Concierge</h1>
            <p className="text-[10px] text-[#9A9590]">Your personal Cape Town food advisor</p>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#A94438]/10 to-[#C4A35A]/10 flex items-center justify-center mb-4">
                <Sparkle weight="duotone" className="w-8 h-8 text-[#A94438]" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-[#1A1918] mb-1">
                Hey{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-sm text-[#7A7873] mb-8 max-w-sm">
                I know your dining history and Cape Town's restaurant scene. Ask me anything.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="text-left text-sm p-3 rounded-xl border border-[#EAE8E3] hover:border-[#A94438]/30 hover:bg-[#F9F8F5] transition-all text-[#5A5752]"
                    data-testid={`suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A94438] to-[#C4A35A] flex items-center justify-center flex-shrink-0 mt-1">
                  <Sparkle weight="fill" className="w-4 h-4 text-white" />
                </div>
              )}
              <div className={`max-w-[80%] sm:max-w-[70%] ${
                msg.role === 'user'
                  ? 'bg-[#A94438] text-white rounded-2xl rounded-br-sm px-4 py-3'
                  : 'bg-[#F5F1EC] text-[#1A1918] rounded-2xl rounded-bl-sm px-4 py-3'
              }`}>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>

                {/* Mentioned restaurants */}
                {msg.mentioned && msg.mentioned.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-black/10 space-y-1.5">
                    {msg.mentioned.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => r.id ? navigate(`/restaurants/${r.id}`) : null}
                        className="flex items-center gap-2 text-xs w-full text-left hover:bg-black/5 rounded-lg px-2 py-1.5 transition-colors"
                        data-testid={`mentioned-${i}`}
                      >
                        <ForkKnife weight="fill" className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate font-medium">{r.name}</span>
                        {r.suburb && <span className="text-[10px] opacity-60 flex-shrink-0">{r.suburb}</span>}
                        {r.in_list ? (
                          <span className="ml-auto text-[10px] opacity-50">In list</span>
                        ) : (
                          <ArrowRight weight="bold" className="w-3 h-3 ml-auto opacity-40 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-[#E8DCD0] flex items-center justify-center flex-shrink-0 mt-1">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <User weight="fill" className="w-4 h-4 text-[#7A7873]" />
                  )}
                </div>
              )}
            </motion.div>
          ))}

          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A94438] to-[#C4A35A] flex items-center justify-center flex-shrink-0">
                <Sparkle weight="fill" className="w-4 h-4 text-white" />
              </div>
              <div className="bg-[#F5F1EC] rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#9A9590] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#9A9590] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#9A9590] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-[#EAE8E3] bg-white p-4" data-testid="chat-input-area">
          <div className="max-w-3xl mx-auto flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about dining in Cape Town..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-[#E8DCD0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#A94438]/20 focus:border-[#A94438]/40 placeholder:text-[#B5B0AA]"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              data-testid="chat-input"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="btn-primary rounded-xl px-4 flex-shrink-0"
              data-testid="send-btn"
            >
              {loading ? (
                <SpinnerGap className="w-5 h-5 animate-spin" />
              ) : (
                <PaperPlaneTilt weight="fill" className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Concierge;
